import React, { useEffect, useState, useCallback, JSX } from "react";
import { strings } from "../../resources/strings";
import { styles } from "../../shared-styles/styles";
import { Tile, ExpandableInstructions } from "../../components";
import { useSelfTriggeredQuestionsWebFlag } from "../../utils/launchdarkly/featureFlags";
import { useMutation, useQuery } from "@apollo/client";
import { GET_PARTICIPANT_CASE } from "../../apis/queries/getParticipantCase";
import {
  Case,
  PendingQuestions,
  StepInput,
  AddOnboardingStepInput,
  CompleteOnboardingInput,
} from "../../apis/API";
import Utils, { getCurrentTimeInSeconds } from "../../utils/UtilityFunctions";
import {
  areQuestionsDue,
  canAnswerQuestionsNow as canAnswerQuestionsNowUtil,
} from "../../utils/questionUtils";
import CheckInProcess from "../../components/Questions/CheckInProcess";
import { useAuthenticator } from "@aws-amplify/ui-react";
import OnboardingProcess from "../../components/Onboarding/OnboardingProcess";
import { ADD_ONBOARDING_STEP } from "../../apis/mutations/addOnboardingStep";
import { v4 as uuid } from "uuid";
import DeviceInfoService from "../../utils/DeviceInfo";
import { COMPLETE_ONBOARDING } from "../../apis/mutations/completeOnboarding";
import { useNavigate } from "react-router-dom";
import { GET_FUTURE_APPOINTMENTS } from "../../apis/queries/getFutureAppointments";
import { Appointment } from "../../apis/API";
import { EventType } from "../../defs/Types";
import {
  formatDateTime,
  formatCountdownText,
} from "../../utils/dateTimeUtils";

// Constants
const INSTRUCTION_LINE_LIMIT = 1; // Truncate instructions at exactly one line for consistent behavior

// Format date/time for dashboard cards with countdown
const formatDashboardDateTime = (timestamp: number) => {
  if (!timestamp || timestamp <= 0) {
    return "";
  }

  const formattedDate = formatDateTime(timestamp);
  const countdownText = formatCountdownText(timestamp);
  return (
    <span>
      <span className="font-bold">When:</span> {formattedDate}{" "}
      <span className="text-slate-600">({countdownText})</span>
    </span>
  );
};

const Dashboard: React.FC<any> = ({
  setIsOnboarded,
  isOnboarded,
}: {
  setIsOnboarded: Function;
  isOnboarded: boolean;
}) => {
  const { user } = useAuthenticator((context) => [context.user]);

  // Feature flag to control self-triggered questions feature
  const isSelfTriggeredQuestionsEnabled = useSelfTriggeredQuestionsWebFlag();

  /**
   * Query to get the Participant's case data
   * - Disabled caching to prevent stale data from being displayed
   * - Uses the `refetch` function to manually refresh the data when the modal is dismissed after submitting check-in/questions
   * - skip will prevent the query from running if the user is not set
   */
  const {
    data: caseData,
    loading: loadingCase,
    error: loadCaseError,
    refetch: refreshCase,
  } = useQuery(GET_PARTICIPANT_CASE, {
    fetchPolicy: "no-cache",
    skip: user ? false : true,
  });

  /**
   * Query to get the Participant's future appointments
   * - Disabled caching to prevent stale data from being displayed
   */
  const {
    loading: apptsLoading,
    error: apptsError,
    data: apptsData,
  } = useQuery(GET_FUTURE_APPOINTMENTS);

  const [appointments, setAppointments] = React.useState<Appointment[]>([]);

  // Navigation hook which is later used to navigate to the appointments page
  const navigate = useNavigate();

  // Passed as a prop to the Onboarding / Check-in modals to track the completed onboarding steps
  const [addOnboardingStepMutation] = useMutation(ADD_ONBOARDING_STEP);
  const [completeOnboardingMutation] = useMutation(COMPLETE_ONBOARDING);

  // Stores the case data once loaded, and controls the dashboard tile loading state
  const [participantCase, setParticipantCase] = useState<Case | null>(null);

  // Controls the opening and closing of the checkin/questions modal
  const [showCheckInModal, setShowCheckInModal] = useState<boolean>(false);

  // Flag to tell the CheckInProcess component that it was launched from onboarding
  const [checkInFromOnboarding, setCheckInFromOnboarding] =
    useState<boolean>(false);

  // Controls the opening / closing of the Onboarding modal
  const [showOnboardingModal, setShowOnboardingModal] =
    useState<boolean>(false);

  // State variables for filtered appointments and court dates
  const [orderedAppointments, setOrderedAppointments] = useState<Appointment[]>(
    [],
  );
  const [courtDates, setCourtDates] = useState<Appointment[]>([]);

  /**
   * - Enabling the Start CheckIn/Questions button by reading the `pendingQuestions` field from the case, if
   *   it is not null, then the tile-button should appear, allowing the user to submit check-ins / questions.
   *
   * - The `pendingQuestions` field is set by the backend when the current date is within the time window in
   *   which questions can be submitted. It is also set if the Officer manually requests question answers via the agency portal.
   */
  const [checkInQuestionsEnabled, setCheckInQuestionsEnabled] =
    useState<boolean>(false);

  // If onboarding, session Id is used for step tracking mutations, as well as the checkInId for the check-in process
  const [sessionId, setSessionId] = useState<string>("");
  const [checkInId, setCheckInId] = useState<string>("");

  // State for self-triggered questions feature
  const [isSelfTriggered, setIsSelfTriggered] = useState<boolean>(false);

  // Determine if "Answer Now" button should appear
  // Only when: flag ON + no pending questions + has question assignment with questions
  const canAnswerQuestionsNow = canAnswerQuestionsNowUtil(
    isSelfTriggeredQuestionsEnabled,
    participantCase,
    checkInQuestionsEnabled,
  );

  const deviceInfo = DeviceInfoService.getInstance().getDeviceInfo();

  /**
   * Sets the participantCase object when the query data is loaded
   * - If the `pendingQuestions` field is not null, then the Start CheckIn/Questions button should be enabled
   * - The child component will call `removeStartCheckInButton` when the modal is dismissed, which will disable the button,
   *   as well as call the `refreshCase` function to rerun the Query to get the latest case data
   */
  useEffect(() => {
    const participantCase: Case | null = caseData?.getParticipantCase;
    const pendingQuestions: PendingQuestions | null | undefined =
      participantCase?.pendingQuestions;
    const onboardingCompletedAt: number | null | undefined =
      participantCase?.onboardingCompletedAt;

    if (participantCase) {
      // Update case state
      setParticipantCase(participantCase);

      // Determines if Onboarding is required
      // - Case returns `onboardingCompletedAt` (epoch date of onboarding)
      // - If null, the user has not onboarded, or needs to onboard again
      if (Utils.isNullOrUndefined(onboardingCompletedAt) && !isOnboarded) {
        displayOnboardingModal();
      }

      // Process the appointment data
      processAppointmentsData(apptsData);

      // Enable / disable CheckInQuestions based on whether questions are currently due
      // This validates that we're within the startTime and answersValidUntil window
      const questionsDue = areQuestionsDue(pendingQuestions);
      setCheckInQuestionsEnabled(questionsDue);
    }
  }, [caseData, apptsData, isOnboarded]);

  /**
   * Processes the appointment data from the query
   * - Maps the data to the Appointment type
   * - Sets the appointments state
   */
  const processAppointmentsData = (
    data: { getFutureAppointments?: Appointment[] } | undefined,
  ) => {
    if (data?.getFutureAppointments && data.getFutureAppointments.length > 0) {
      const futureAppointments: Appointment[] = data.getFutureAppointments.map(
        (appointment: Appointment) => appointment,
      );
      setAppointments(futureAppointments);

      // Filter the appointments and court dates immediately after setting appointments
      const filteredOrderedAppointments = futureAppointments.filter(
        (appointment) => appointment.type === EventType.Appointment,
      );
      const filteredCourtDates = futureAppointments.filter(
        (appointment) => appointment.type === EventType.CourtDate,
      );

      setOrderedAppointments(filteredOrderedAppointments);
      setCourtDates(filteredCourtDates);
    } else {
      // Reset arrays when no appointments are available
      setAppointments([]);
      setOrderedAppointments([]);
      setCourtDates([]);
    }
  };

  /**
   * Modal State Controls
   * - CheckIn/Questions Modal
   * - Onboarding Modal
   */

  const startCheckInProcess = () => {
    setIsSelfTriggered(false);
    setCheckInId(uuid());
    setShowCheckInModal(true);
  };

  /**
   * Launches self-triggered questions flow (no photo required)
   * - Sets isSelfTriggered to true to indicate this is a voluntary check-in
   * - promptId will be passed as null to CheckInProcess
   */
  const startSelfTriggeredQuestions = () => {
    setIsSelfTriggered(true);
    setCheckInId(uuid());
    setShowCheckInModal(true);
  };

  const dismissQuestionsModal = () => {
    setIsOnboarded(true);
    setShowCheckInModal(false);
    setIsSelfTriggered(false);
  };

  /**
   * Displays the Onboarding modal and sets the session Id to be provided as the checkInId
   * after accepting the terms of use
   */
  const displayOnboardingModal = () => {
    setSessionId(uuid());
    setShowOnboardingModal(true);
  };

  /**
   * Launches the Check-In process from the Onboarding modal after accepting the terms of use
   */
  const launchCheckInFromOnboarding = () => {
    // Dismiss the onboarding / terms of use modal
    setShowOnboardingModal(false);

    // Launch checkin with flag indicating it was launched from onboarding
    setCheckInFromOnboarding(true);

    setShowCheckInModal(true);
  };

  /**
   * Hides the `START` button in the "Next Questions" tile after the Check-In/Questions have been submitted
   * and the modal has been dismissed. Called by the child component `CheckInQuestionsModal`
   */
  const removeStartCheckInButton = () => {
    setCheckInQuestionsEnabled(false);
  };

  const addOnboardingStep = (step: StepInput) => {
    const input: AddOnboardingStepInput = {
      sessionId: sessionId,
      step: step,
      workflow: "Onboarding",
      time: getCurrentTimeInSeconds(),
      deviceInfo: JSON.stringify(deviceInfo),
    };
    performAddOnboardingStepMutation(input);
  };
  /**
   * Mutation to track the completed onboarding steps
   * step names: `Terms and Conditions` | `Photo` | `Check-in`
   */
  const performAddOnboardingStepMutation = async (
    input: AddOnboardingStepInput,
  ) => {
    try {
      await addOnboardingStepMutation({
        variables: {
          input: {
            sessionId: input.sessionId,
            step: input.step,
            workflow: input.workflow,
            time: input.time,
            deviceInfo: input.deviceInfo,
          },
        },
      });
    } catch (error) {
      console.error("Error saving raw event: ", error);
    }
  };

  const completeOnboarding = async () => {
    const input: CompleteOnboardingInput = {
      sessionId: sessionId,
      time: getCurrentTimeInSeconds(),
      deviceInfo: JSON.stringify(deviceInfo),
    };

    try {
      await completeOnboardingMutation({
        variables: {
          input: {
            sessionId: input.sessionId,
            time: input.time,
            deviceInfo: input.deviceInfo,
          },
        },
      });
    } catch (error) {
      console.error("Error saving onboarding-completed: ", error);
    }
  };

  const getDetailedContent = useCallback(
    (
      item: Appointment,
      noItemsMessage: string,
      showAppointmentType: boolean = false,
    ): JSX.Element => {
      const instructionsText = item.appointmentInstructions || item.notes || "";

      return (
        <div className="flex flex-col space-y-4">
          {showAppointmentType && item.resolvedAppointmentType?.label && (
            <div className="text-left">
              <span className="font-bold">What:</span>{" "}
              {item.resolvedAppointmentType.label}
            </div>
          )}
          <div className="text-left">
            {item.time && (
              <>
                {(() => {
                  const formattedDateTime = formatDateTime(item.time);
                  const countdownText = formatCountdownText(item.time);
                  const formattedCountdownText = `(${countdownText})`;

                  if (formattedDateTime.includes("between")) {
                    return (
                      <div>
                        <div>
                          <span className="font-bold">When:</span>{" "}
                          {formattedDateTime}
                        </div>
                        <div className="ml-14 text-slate-600">
                          {formattedCountdownText}
                        </div>
                      </div>
                    );
                  } else {
                    return (
                      <span>
                        <span className="font-bold">When:</span>{" "}
                        {formattedDateTime}{" "}
                        <span className="text-slate-600">
                          {formattedCountdownText}
                        </span>
                      </span>
                    );
                  }
                })()}
              </>
            )}
          </div>
          {item.location && (
            <>
              <div className="text-left">
                <div>
                  <span className="font-bold">Where:</span>{" "}
                  {(item.location.number || item.location.street) && (
                    <>
                      {item.location.number ? `${item.location.number} ` : ""}
                      {item.location.street || ""}
                    </>
                  )}
                </div>
                {(item.location.city ||
                  item.location.state ||
                  item.location.postalCode) && (
                  <div className="ml-14">
                    {item.location.city ? `${item.location.city}` : ""}
                    {item.location.city && item.location.state ? ", " : ""}
                    {item.location.state ? `${item.location.state} ` : ""}
                    {item.location.postalCode || ""}
                  </div>
                )}
                {item.location.name && (
                  <div className="font-bold mt-1">{item.location.name}</div>
                )}
              </div>
            </>
          )}
          <div className="text-left">
            <span className="font-bold">Instructions:</span>{" "}
            <ExpandableInstructions
              instructionsText={instructionsText}
              lineLimit={INSTRUCTION_LINE_LIMIT}
              textColor="text-slate-600"
            />
          </div>
        </div>
      );
    },
    [],
  );

  const getDetailedCourtDateContent = useCallback(
    (courtDates: Appointment[]): string | JSX.Element => {
      if (!courtDates || courtDates.length === 0) {
        return strings.appointments.noUpcomingCourtDates;
      }

      return getDetailedContent(
        courtDates[0],
        strings.appointments.noUpcomingCourtDates,
        false,
      );
    },
    [getDetailedContent],
  );

  const getDetailedAppointmentContent = useCallback(
    (appointments: Appointment[]): string | JSX.Element => {
      if (!appointments || appointments.length === 0) {
        return strings.appointments.noUpcomingAppts;
      }

      return getDetailedContent(
        appointments[0],
        strings.appointments.noUpcomingAppts,
        true,
      );
    },
    [getDetailedContent],
  );

  return (
    <main data-theme="repath">
      {/* Displays a toast error if an error is thrown while querying the case */}
      {loadCaseError && (
        <>
          {console.error(
            "Failed to load participant case, error: ",
            loadCaseError,
          )}
          {Utils.toastError(strings.toast.loadCaseError)}
        </>
      )}

      {(participantCase || loadingCase) && (
        <>
          <div className="prose prose-base pl-5 pt-6">
            <h2 className="font-normal text-4xl">
              👋 Hello,{" "}
              {participantCase?.participant?.firstName ||
                (loadingCase ? "..." : "there")}
            </h2>
          </div>

          {/* Info tiles - Three column horizontal layout */}
          <div className="grid lg:grid-cols-3 md:grid-cols-2 sm:grid-cols-1 gap-4 px-5 py-5 pt-20">
            {/* Left Column: Check-In */}
            <div>
              <div className={`${styles.pageHeader} pb-6`}>
                <h2>Check-In</h2>
              </div>
              <Tile
                title={
                  checkInQuestionsEnabled
                    ? strings.questionsDue
                    : strings.nextQuestionsDate
                }
                icon={
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="w-8 h-8"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 0 0 2.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 0 0-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c0 .621.504 1.125 1.125 1.125H18a2.25 2.25 0 0 0 2.25-2.25M8.25 8.25h2.25"
                    />
                  </svg>
                }
                content={
                  checkInQuestionsEnabled
                    ? ""
                    : participantCase?.nextQuestions?.time
                      ? formatDashboardDateTime(
                          participantCase.nextQuestions.time,
                        )
                      : ""
                }
                buttonText={strings.start}
                onClick={startCheckInProcess}
                isTileButtonEnabled={checkInQuestionsEnabled}
                secondaryButtonText={strings.answerNow}
                onSecondaryClick={startSelfTriggeredQuestions}
                isSecondaryButtonEnabled={canAnswerQuestionsNow}
                isLoading={loadingCase}
              />
            </div>

            {/* Center Column: Court */}
            <div>
              <div className={`${styles.pageHeader} pb-6`}>
                <h2>Court</h2>
              </div>
              <Tile
                title={"Next Court Date"}
                icon={
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="w-6 h-6"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 21v-8.25M15.75 21v-8.25M8.25 21v-8.25M3 9l9-6 9 6m-1.5 12V10.332A48.36 48.36 0 0012 9.75c-2.551 0-5.056.2-7.5.582V21M3 21h18M12 6.75h.008v.008H12V6.75z"
                    />
                  </svg>
                }
                content={
                  appointments && !apptsLoading && courtDates?.length > 0
                    ? getDetailedCourtDateContent(courtDates)
                    : strings.appointments.noUpcomingCourtDates
                }
                buttonText={
                  courtDates?.length > 1
                    ? `See All (${courtDates.length})`
                    : strings.appointments.courtHeader
                }
                onClick={() => {
                  navigate("/appointments");
                }}
                isTileButtonEnabled={
                  appointments && !apptsLoading && courtDates?.length > 1
                }
                isLoading={apptsLoading}
              />
            </div>

            {/* Right Column: Appointments */}
            <div>
              <div className={`${styles.pageHeader} pb-6`}>
                <h2>Appointments</h2>
              </div>
              <Tile
                title={"Next Appointment Date"}
                icon={
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="w-6 h-6"
                    aria-label="Calendar icon"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5m-9-6h.008v.008H12v-.008zM12 15h.008v.008H12V15zm0 2.25h.008v.008H12v-.008zM9.75 15h.008v.008H9.75V15zm0 2.25h.008v.008H9.75v-.008zM7.5 15h.008v.008H7.5V15zm0 2.25h.008v.008H7.5v-.008zm6.75-4.5h.008v.008h-.008v-.008zm0 2.25h.008v.008h-.008V15zm0 2.25h.008v.008h-.008v-.008zm2.25-4.5h.008v.008H16.5v-.008zm0 2.25h.008v.008H16.5V15z"
                    />
                  </svg>
                }
                content={
                  appointments &&
                  !apptsLoading &&
                  orderedAppointments?.length > 0
                    ? getDetailedAppointmentContent(orderedAppointments)
                    : strings.appointments.noUpcomingAppts
                }
                buttonText={
                  orderedAppointments?.length > 1
                    ? `See All (${orderedAppointments.length})`
                    : strings.appointments.apptsHeader
                }
                onClick={() => {
                  navigate("/appointments");
                }}
                isTileButtonEnabled={
                  appointments &&
                  !apptsLoading &&
                  orderedAppointments?.length > 1
                }
                isLoading={apptsLoading}
              />
            </div>
          </div>

          {/* Handle appointments loading error */}
          {apptsError && (
            <>
              {console.error(apptsError)}
              {Utils.toastError(strings.toast.loadAppointmentsError)}
            </>
          )}
        </>
      )}

      <OnboardingProcess
        isOpen={showOnboardingModal}
        participantCase={participantCase}
        refreshCase={refreshCase}
        addOnboardingStep={addOnboardingStep}
        launchCheckInFromOnboarding={launchCheckInFromOnboarding}
      />

      <CheckInProcess
        isOpen={showCheckInModal}
        isOnboarding={checkInFromOnboarding}
        isSelfTriggered={isSelfTriggered}
        sessionId={sessionId}
        checkInId={checkInId}
        participantCase={participantCase}
        dismiss={dismissQuestionsModal}
        removeStartCheckInButton={removeStartCheckInButton}
        refreshCase={refreshCase}
        addOnboardingStep={addOnboardingStep}
        onboardingCompleted={completeOnboarding}
      />
    </main>
  );
};

export default Dashboard;
